import { Alert, Button, Card, Empty, Input, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import request from "@/api";
import { userService } from "@/api/services/userService";
import { ABLE } from "@/meta/common";
import { getMyTaskNewsNavigation, prepareMyTaskNewsNavigation, type MyTaskNewsOperation } from "./MyTaskAction";
import { moduleData } from "./newsList/module";
import type { MenuItem } from "@/entity/mytask/recentNews";

interface MyTaskNewsLog {
    operationLog?: {
        content?: string;
        createdTime?: string;
        highLight?: string;
        bizNo?: string;
        operationModule?: number;
        operationType?: number;
    };
}

interface MyTaskNewsPageResult {
    list?: MyTaskNewsLog[];
    total?: number;
}

interface MyTaskNewsGroup {
    date: string;
    list: MyTaskNewsLog[];
    collapsed: boolean;
}

export interface MyTaskNewsUserOption {
    value: number;
    label: string;
}

type NewsDateType = "all" | "today" | "yesterday" | "thisWeek" | "lastWeek" | "thisMonth" | "lastMonth";

export interface MyTaskNewsListPageProps {
    loadNews?: (params: {
        page: number;
        size: number;
        startTime: string;
        endTime: string;
        userId?: number;
        operationModule?: number[];
    }) => Promise<MyTaskNewsPageResult>;
    loadNewsUsers?: (keyword: string) => Promise<MyTaskNewsUserOption[]>;
    loadNewsDetail?: (params: { id: string; module: string }) => Promise<boolean>;
}

/** 复用 Vue 动态列表的操作日志分页接口。 */
export async function loadMyTaskNewsList(params: {
    page: number;
    size: number;
    startTime: string;
    endTime: string;
    userId?: number;
    operationModule?: number[];
}) {
    const response = await request.service.get("/admin/operationLog/paging", { params });
    return response.data as MyTaskNewsPageResult;
}

/** 复用 Vue 高亮动态跳转前的数据存在性校验接口。 */
export async function loadMyTaskNewsListDetail(params: { id: string; module: string }) {
    const response = await request.service.get("/admin/operationLog/findByIdAndModule", { params });
    return Boolean(response.data);
}

/** 沿用 Vue 动态列表启用用户选择器的 name/state 查询契约。 */
export async function loadMyTaskNewsUsers(keyword: string): Promise<MyTaskNewsUserOption[]> {
    const users = await userService.userSelectorByState({ name: keyword, state: ABLE.ENABLE.code });
    return users.map(user => ({ value: user.id, label: user.name }));
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

/** 对齐 Vue newsBox：当天/昨天显示中文标题，其他日期显示斜线月日。 */
export const formatMyTaskNewsGroupDate = (date: string, now = new Date()) => {
    const groupDate = new Date(`${date}T00:00:00`);
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const month = String(groupDate.getMonth() + 1).padStart(2, "0");
    const day = String(groupDate.getDate()).padStart(2, "0");
    if (date === formatDate(now)) {
        return { title: "今天", label: `${month}月${day}日` };
    }
    if (date === formatDate(yesterday)) {
        return { title: "昨天", label: `${month}/${day}` };
    }
    return { title: "", label: `${month}/${day}` };
};

/** 根据 Vue 日期选项生成操作日志查询范围。 */
export const getMyTaskNewsDateRange = (type: NewsDateType, now = new Date()) => {
    const end = new Date(now);
    const start = new Date(now);
    if (type === "yesterday") {
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
    }
    if (type === "thisWeek") {
        const day = start.getDay() || 7;
        start.setDate(start.getDate() - day + 1);
    }
    if (type === "lastWeek") {
        const day = start.getDay() || 7;
        start.setDate(start.getDate() - day - 6);
        end.setDate(end.getDate() - day);
    }
    if (type === "thisMonth") start.setDate(1);
    if (type === "lastMonth") {
        start.setDate(1);
        start.setMonth(start.getMonth() - 1);
        end.setDate(1);
        end.setDate(end.getDate() - 1);
    }
    if (type === "all") {
        start.setFullYear(2024, 10, 15);
    }
    return {
        startTime: `${formatDate(start)} 00:00:00`,
        endTime: `${formatDate(end)} 23:59:59`
    };
};

const stripDatePrefix = (content?: string) =>
    content?.replace(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}\s+/, "").trim() || "暂无数据";

/**
 * 对齐旧 Vue 级联模块筛选：选择父模块时，把查询范围展开为全部末级模块。
 * 只有末级模块值才是后端操作日志的真实枚举，父级值不能直接提交。
 */
export const getMyTaskNewsModuleIds = (selectedModule: number): number[] => {
    const findModule = (options: MenuItem[]): MenuItem | undefined => {
        for (const option of options) {
            if (option.value === selectedModule) return option;
            const matched = option.children ? findModule(option.children) : undefined;
            if (matched) return matched;
        }
        return undefined;
    };
    const selected = findModule(moduleData);
    if (!selected) return [];
    const collectLeafValues = (option: MenuItem): number[] =>
        option.children?.length ? option.children.flatMap(collectLeafValues) : [option.value];
    return collectLeafValues(selected);
};

/** 沿用 Vue newsBox 的按操作日志日期分组口径。 */
const getNewsDate = (item: MyTaskNewsLog) => {
    const createdTime = item.operationLog?.createdTime?.split("T")[0];
    const contentDate = item.operationLog?.content?.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    return createdTime || contentDate || "未知日期";
};

/** 将接口日志按日期聚合，保留接口返回顺序以匹配 Vue 页面。 */
const groupNewsByDate = (list: MyTaskNewsLog[]): MyTaskNewsGroup[] => {
    const groups = new Map<string, MyTaskNewsGroup>();
    list.forEach(item => {
        const date = getNewsDate(item);
        const group = groups.get(date);
        if (group) {
            group.list.push(item);
            return;
        }
        groups.set(date, { date, list: [item], collapsed: false });
    });
    return [...groups.values()];
};

/** Vue 动态列表基础页，保留筛选、请求参数、日志分组基础展示和空态。 */
export function MyTaskNewsListPage({
    loadNews = loadMyTaskNewsList,
    loadNewsUsers = loadMyTaskNewsUsers,
    loadNewsDetail = loadMyTaskNewsListDetail
}: MyTaskNewsListPageProps) {
    const navigate = useNavigate();
    const [dateType, setDateType] = useState<NewsDateType>("today");
    const [draftUserId, setDraftUserId] = useState("");
    const [draftUserKeyword, setDraftUserKeyword] = useState("");
    const [userOptions, setUserOptions] = useState<MyTaskNewsUserOption[]>([]);
    const [draftModuleId, setDraftModuleId] = useState("");
    const [filters, setFilters] = useState<{ userId?: number; operationModule?: number[] }>({});
    const [newsGroups, setNewsGroups] = useState<MyTaskNewsGroup[]>([]);
    const [newsActionError, setNewsActionError] = useState("");
    const dateOptions: Array<{ value: NewsDateType; label: string }> = [
        { value: "all", label: "全部" },
        { value: "today", label: "今天" },
        { value: "yesterday", label: "昨天" },
        { value: "thisWeek", label: "本周" },
        { value: "lastWeek", label: "上周" },
        { value: "thisMonth", label: "本月" },
        { value: "lastMonth", label: "上月" }
    ];

    useEffect(() => {
        let disposed = false;
        const range = getMyTaskNewsDateRange(dateType);
        void loadNews({ page: 1, size: 5000, ...range, ...filters }).then(result => {
            if (!disposed) setNewsGroups(groupNewsByDate(result.list || []));
        });
        return () => {
            disposed = true;
        };
    }, [dateType, filters, loadNews]);

    const applyFilters = () => {
        const userId = Number(draftUserId);
        const moduleId = Number(draftModuleId);
        const moduleIds = Number.isFinite(moduleId) && moduleId > 0 ? getMyTaskNewsModuleIds(moduleId) : [];
        setFilters({
            ...(Number.isFinite(userId) && userId > 0 ? { userId } : {}),
            ...(moduleIds.length ? { operationModule: moduleIds } : {})
        });
    };

    /** 关键词走 Vue 启用用户远程选择器，纯数字继续允许直接作为用户 ID 查询。 */
    const handleUserKeywordChange = (value: string) => {
        setDraftUserKeyword(value);
        setDraftUserId(/^\d+$/.test(value.trim()) ? value.trim() : "");
        if (!value.trim() || /^\d+$/.test(value.trim())) {
            setUserOptions([]);
            return;
        }
        void loadNewsUsers(value.trim())
            .then(setUserOptions)
            .catch(() => {
                setUserOptions([]);
                setNewsActionError("用户搜索失败");
            });
    };

    /** 选择远程候选后只提交候选 ID，输入框回显用户名称。 */
    const selectNewsUser = (option: MyTaskNewsUserOption) => {
        setDraftUserId(String(option.value));
        setDraftUserKeyword(option.label);
        setUserOptions([]);
    };

    /** 只切换当前日期块，避免影响其他日期的展开状态。 */
    const toggleNewsGroup = (date: string) => {
        setNewsGroups(groups =>
            groups.map(group => (group.date === date ? { ...group, collapsed: !group.collapsed } : group))
        );
    };

    /** 沿用 Vue onReset，清空输入并恢复当天筛选。 */
    const resetFilters = () => {
        setDraftUserId("");
        setDraftUserKeyword("");
        setUserOptions([]);
        setDraftModuleId("");
        setFilters({});
        setDateType("today");
    };

    /** 沿用 Vue newsItem：详情不存在时提示并保持动态列表不变。 */
    const handleNewsHighlight = async (item: MyTaskNewsLog) => {
        const operation = item.operationLog as (MyTaskNewsOperation & { highLight?: string }) | undefined;
        const target = operation ? getMyTaskNewsNavigation(operation) : undefined;
        if (!target || !operation?.highLight || !operation.bizNo || operation.operationModule === undefined) return;
        setNewsActionError("");
        try {
            if (operation.operationType !== 3) {
                const exists = await loadNewsDetail({ id: operation.bizNo, module: String(operation.operationModule) });
                if (!exists) {
                    setNewsActionError("该数据不存在");
                    return;
                }
            }
            prepareMyTaskNewsNavigation(operation);
            await navigate(target.path);
        } catch {
            setNewsActionError("该数据不存在");
        }
    };

    return (
        <section aria-label="my-task-news-list-page" className="flex min-h-full flex-col gap-4">
            <Card title="最新动态">
                {newsActionError ? <Alert message={newsActionError} role="alert" type="error" /> : null}
                <Space className="mb-4" wrap>
                    {dateOptions.map(item => (
                        <Button
                            key={item.value}
                            aria-label={item.label}
                            type={dateType === item.value ? "primary" : "default"}
                            onClick={() => setDateType(item.value)}
                        >
                            {item.label}
                        </Button>
                    ))}
                    <Input
                        aria-label="用户ID"
                        placeholder="请选择用户或输入用户ID"
                        value={draftUserKeyword}
                        onChange={event => handleUserKeywordChange(event.target.value)}
                    />
                    {userOptions.map(option => (
                        <div
                            key={option.value}
                            role="option"
                            aria-selected={draftUserId === String(option.value)}
                            className="cursor-pointer p-2"
                            onClick={() => selectNewsUser(option)}
                        >
                            {option.label}
                        </div>
                    ))}
                    <Input
                        aria-label="模块ID"
                        placeholder="模块ID"
                        value={draftModuleId}
                        onChange={event => setDraftModuleId(event.target.value)}
                    />
                    <Button aria-label="查询动态" type="primary" onClick={applyFilters}>
                        查询动态
                    </Button>
                    <Button aria-label="重置动态" onClick={resetFilters}>
                        重置
                    </Button>
                </Space>
                {newsGroups.length ? (
                    <Space direction="vertical" size="middle" className="w-full">
                        {newsGroups.map(group => (
                            <section key={group.date} aria-label={`动态日期 ${group.date}`}>
                                <Space align="center" className="mb-2">
                                    {(() => {
                                        const displayDate = formatMyTaskNewsGroupDate(group.date);
                                        return (
                                            <>
                                                {displayDate.title ? (
                                                    <Typography.Text strong>{displayDate.title}</Typography.Text>
                                                ) : null}
                                                <Typography.Text strong>{displayDate.label}</Typography.Text>
                                                {/* 保留原始日期供无障碍和旧测试定位，不改变 Vue 对用户的可见格式。 */}
                                                <span className="sr-only">{group.date}</span>
                                            </>
                                        );
                                    })()}
                                    <Button
                                        type="link"
                                        size="small"
                                        aria-label={`${group.collapsed ? "展开" : "收起"} ${group.date}`}
                                        onClick={() => toggleNewsGroup(group.date)}
                                    >
                                        {group.collapsed ? "展开" : "收起"}
                                    </Button>
                                </Space>
                                {!group.collapsed && (
                                    <Space direction="vertical" size="small" className="w-full">
                                        {group.list.map((item, index) => {
                                            const content = item.operationLog?.content;
                                            const contentWithoutDate = stripDatePrefix(content);
                                            const highlight = item.operationLog?.highLight;
                                            const highlightIndex = highlight
                                                ? contentWithoutDate.indexOf(highlight)
                                                : -1;
                                            const canNavigate =
                                                highlightIndex >= 0 &&
                                                Boolean(
                                                    item.operationLog &&
                                                    getMyTaskNewsNavigation(item.operationLog as MyTaskNewsOperation)
                                                );
                                            return (
                                                <Space key={`${content || "news"}-${index}`} align="start">
                                                    <Typography.Text type="secondary">
                                                        {content?.split(" ")[1] || "--:--"}
                                                    </Typography.Text>
                                                    <Typography.Text>
                                                        {canNavigate ? (
                                                            <>
                                                                {contentWithoutDate.slice(0, highlightIndex)}
                                                                <Button
                                                                    aria-label={`查看动态：${highlight}`}
                                                                    className="h-auto p-0"
                                                                    type="link"
                                                                    onClick={() => void handleNewsHighlight(item)}
                                                                >
                                                                    {highlight}
                                                                </Button>
                                                                {contentWithoutDate.slice(
                                                                    highlightIndex + highlight!.length
                                                                )}
                                                            </>
                                                        ) : (
                                                            contentWithoutDate
                                                        )}
                                                    </Typography.Text>
                                                </Space>
                                            );
                                        })}
                                    </Space>
                                )}
                            </section>
                        ))}
                    </Space>
                ) : (
                    <Empty description="暂无数据" />
                )}
            </Card>
            {/* 对齐旧 Vue operation-fixed-btn，返回动态列表前的历史页面。 */}
            <div className="fixed bottom-[30px] left-[36px]">
                <Button aria-label="返回" size="large" type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </div>
        </section>
    );
}
